import { useState, useEffect, useMemo, useCallback } from "react";
import type { DateRange } from "react-day-picker";
import type { TWRChart } from "@/schemas";
import { getTWRChart } from "../services/portfolio-service";
import { rebaseTWRChart, sliceAndRebaseTWR } from "../utils/twr-utils";
import { formatDateRange } from "../utils/format-utils";

interface UseTWRDataResult {
  twrChartData: TWRChart | null;
  displayTwrChartData: TWRChart | null;
  loading: boolean;
  // Range state
  rangeEnabled: boolean;
  setRangeEnabled: (enabled: boolean) => void;
  range: DateRange | undefined;
  setRange: (range: DateRange | undefined) => void;
  rangePopoverOpen: boolean;
  setRangePopoverOpen: (open: boolean) => void;
  // Derived
  fetchPeriod: string;
  formattedRange: string;
  // Metrics
  lastTwr: number;
  mode: string;
  annualizedReturn: number | null;
  // Formatters
  formatTwrXAxis: (timestamp: string) => string;
}

/**
 * Hook to manage TWR data fetching and custom range logic.
 */
export function useTWRData(
  selectedBookId: string,
  period: string,
): UseTWRDataResult {
  const [twrChartData, setTwrChartData] = useState<TWRChart | null>(null);
  const [loading, setLoading] = useState(true);

  // Custom range state
  const [rangeEnabled, setRangeEnabled] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [rangePopoverOpen, setRangePopoverOpen] = useState(false);

  // Derive fetch period: use "max" (daily data) when custom range is enabled
  const fetchPeriod = rangeEnabled ? "max" : period;

  // Fetch TWR chart
  useEffect(() => {
    const fetchTwrData = async () => {
      setLoading(true);
      const twrRes = await getTWRChart(selectedBookId, fetchPeriod);
      setTwrChartData(twrRes);
      setLoading(false);
    };
    fetchTwrData();
  }, [selectedBookId, fetchPeriod]);

  // Compute display TWR data (always rebased to start at 0 on the visible range)
  const displayTwrChartData = useMemo(() => {
    if (!twrChartData) return null;
    if (!rangeEnabled || !range?.from) return rebaseTWRChart(twrChartData);
    return sliceAndRebaseTWR(twrChartData, range);
  }, [twrChartData, rangeEnabled, range]);

  // Format date range for display
  const formattedRange = useMemo(() => formatDateRange(range), [range]);

  // TWR metrics
  const { lastTwr, mode, annualizedReturn } = useMemo(() => {
    if (!displayTwrChartData || !displayTwrChartData.twr_points?.length) {
      return {
        lastTwr: 0,
        mode: displayTwrChartData?.twr_mode ?? "advanced",
        annualizedReturn: null,
      };
    }
    const lastPoint =
      displayTwrChartData.twr_points[displayTwrChartData.twr_points.length - 1];
    return {
      lastTwr: lastPoint.twr_cumulative,
      mode: displayTwrChartData.twr_mode,
      annualizedReturn:
        displayTwrChartData.annualized_return_since_inception ?? null,
    };
  }, [displayTwrChartData]);

  // Format X-axis for TWR chart
  const formatTwrXAxis = useCallback(
    (timestamp: string) => {
      const date = new Date(timestamp);
      const effectivePeriod = rangeEnabled ? "max" : period;

      if (effectivePeriod === "1d") {
        return date.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (effectivePeriod === "1w" || effectivePeriod === "1m") {
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
      // For "max", "1y", or custom range: show month + year
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: "2-digit",
      });
    },
    [rangeEnabled, period],
  );

  return {
    twrChartData,
    displayTwrChartData,
    loading,
    rangeEnabled,
    setRangeEnabled,
    range,
    setRange,
    rangePopoverOpen,
    setRangePopoverOpen,
    fetchPeriod,
    formattedRange,
    lastTwr,
    mode,
    annualizedReturn,
    formatTwrXAxis,
  };
}
